/**
 * MKS-464 — the expansion panel of a Delivery row: tasks delivered in the period, grouped by
 * Prague day (newest day first), then the tasks the person specified (grouped by delivery day,
 * each with small role tags: created / ACs / answered / description), then the tasks they
 * contributed to (grouped by delivery day, each with their own hours), then the tasks currently in
 * progress. Every task links to its detail page and shows code, name and project; `showAssignee`
 * adds who holds it (project and Factory/Robot rows, where the assignee is not implied by the
 * row). A Factory/Robot delivery with co-authors also shows "specified by <names>".
 */
import { Link } from 'react-router-dom';
import type {
  DeliveryContributedTaskRef,
  DeliverySpecRole,
  DeliverySpecifiedTaskRef,
  DeliveryTaskRef,
} from '../../services/api/delivery';
import { formatDisplayDate, formatHours } from '../../utils/format';

export interface DeliveryTaskListProps {
  /** Bot deliveries carry `specifiedBy`; every other list passes plain refs. */
  deliveredTasks: Array<DeliveryTaskRef & { specifiedBy?: string[] }>;
  specifiedTasks?: DeliverySpecifiedTaskRef[];
  contributedTasks?: DeliveryContributedTaskRef[];
  inProgressTasks: DeliveryTaskRef[];
  showAssignee?: boolean;
}

/** Tag order and labels (SD-16); mirrors the backend's DELIVERY_SPEC_ROLES order. */
const SPEC_ROLE_ORDER: DeliverySpecRole[] = ['created', 'acceptance', 'answered', 'description'];
const SPEC_ROLE_LABELS: Record<DeliverySpecRole, string> = {
  created: 'created',
  acceptance: 'ACs',
  answered: 'answered',
  description: 'description',
};

const HEADING_CLASS =
  'px-2 mb-0.5 text-[10.5px] uppercase tracking-wider font-medium text-ms-text-muted';

function TaskItem({
  task,
  showAssignee,
  hours,
  roles,
  specifiedBy,
}: {
  task: DeliveryTaskRef;
  showAssignee: boolean;
  hours?: number;
  roles?: DeliverySpecRole[];
  specifiedBy?: string[];
}) {
  return (
    <li>
      <Link
        to={`/tasks/${task.id}`}
        className="flex items-baseline gap-2 px-2 py-1 rounded-sm text-[12px] hover:bg-ms-surface-2/60 focus:outline-none focus:ring-2 focus:ring-ms-orange/40"
      >
        <span className="font-mono text-[11px] text-ms-text-secondary shrink-0">{task.code}</span>
        <span className="text-ms-text-strong truncate">{task.name}</span>
        <span className="text-ms-text-muted text-[11px] shrink-0">{task.projectName}</span>
        {roles &&
          SPEC_ROLE_ORDER.filter((role) => roles.includes(role)).map((role) => (
            <span
              key={role}
              className="shrink-0 rounded-full bg-ms-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-ms-text-secondary border border-ms-border-subtle"
            >
              {SPEC_ROLE_LABELS[role]}
            </span>
          ))}
        {specifiedBy && specifiedBy.length > 0 && (
          <span data-testid={`specified-by-${task.id}`} className="text-ms-text-muted text-[11px] shrink-0">
            specified by {specifiedBy.join(', ')}
          </span>
        )}
        {showAssignee && (
          <span className="ml-auto text-ms-text-secondary text-[11px] shrink-0">
            {task.assigneeName ?? 'Unassigned'}
          </span>
        )}
        {hours !== undefined && (
          <span className="ml-auto text-ms-text-muted text-[11px] tabular-nums shrink-0">
            {formatHours(hours)} h
          </span>
        )}
      </Link>
    </li>
  );
}

/** Tasks grouped by Prague delivery day, newest day first; header `<date> · <count>`. */
function DayGroups<T extends DeliveryTaskRef>({
  tasks,
  showAssignee,
  hoursOf,
  rolesOf,
  specifiedByOf,
}: {
  tasks: T[];
  showAssignee: boolean;
  hoursOf?: (task: T) => number;
  rolesOf?: (task: T) => DeliverySpecRole[];
  specifiedByOf?: (task: T) => string[] | undefined;
}) {
  const byDay = new Map<string, T[]>();
  for (const task of tasks) {
    const day = task.deliveredDay ?? '';
    const list = byDay.get(day) ?? [];
    list.push(task);
    byDay.set(day, list);
  }
  const days = [...byDay.keys()].sort().reverse();
  return (
    <>
      {days.map((day) => {
        const dayTasks = byDay.get(day) ?? [];
        return (
          <section key={day || 'unknown'}>
            <h4 className={HEADING_CLASS}>
              {formatDisplayDate(day)} · {dayTasks.length}
            </h4>
            <ul>
              {dayTasks.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  showAssignee={showAssignee}
                  hours={hoursOf ? hoursOf(task) : undefined}
                  roles={rolesOf ? rolesOf(task) : undefined}
                  specifiedBy={specifiedByOf ? specifiedByOf(task) : undefined}
                />
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
}

export function DeliveryTaskList({
  deliveredTasks,
  specifiedTasks = [],
  contributedTasks = [],
  inProgressTasks,
  showAssignee = false,
}: DeliveryTaskListProps) {
  if (
    deliveredTasks.length === 0 &&
    specifiedTasks.length === 0 &&
    contributedTasks.length === 0 &&
    inProgressTasks.length === 0
  ) {
    return (
      <p className="px-2 py-1 text-[12px] text-ms-text-muted">
        Nothing delivered or in progress in this period.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <DayGroups
        tasks={deliveredTasks}
        showAssignee={showAssignee}
        specifiedByOf={(task) => task.specifiedBy}
      />
      {specifiedTasks.length > 0 && (
        <section data-testid="delivery-specified" className="space-y-3">
          <h4 className={HEADING_CLASS}>Specified ({specifiedTasks.length})</h4>
          <DayGroups
            tasks={specifiedTasks}
            showAssignee={showAssignee}
            rolesOf={(task) => task.specRoles}
          />
        </section>
      )}
      {contributedTasks.length > 0 && (
        <section data-testid="delivery-contributed" className="space-y-3">
          <h4 className={HEADING_CLASS}>Contributed to ({contributedTasks.length})</h4>
          <DayGroups
            tasks={contributedTasks}
            showAssignee={showAssignee}
            hoursOf={(task) => task.contributedHours}
          />
        </section>
      )}
      {inProgressTasks.length > 0 && (
        <section>
          <h4 className={HEADING_CLASS}>
            In progress ({inProgressTasks.length})
          </h4>
          <ul>
            {inProgressTasks.map((task) => (
              <TaskItem key={task.id} task={task} showAssignee={showAssignee} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
